// src/components/LegendPanel.js
import React from "react";
import { connect } from "react-redux";

import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import CloseIcon from "@mui/icons-material/Close";
import LegendToggleIcon from "@mui/icons-material/FormatListBulleted";

import preloaded from "../utils/data.json";

const mapStateToProps = (state) => ({
  map: state.MapReducer,
});

function isGridLayerId(id) {
  return (id || "").includes("grid") || (id || "").includes("kulonb");
}

function stripNs(id) {
  return (id || "").replace("allum_project:", "");
}

function getLayerTitleFromId(layerId) {
  const short = stripNs(layerId);
  const f = preloaded.find((o) => o.name === short);
  return f?.title || short || "";
}

function getMainLegendTitle(map) {
  if (isGridLayerId(map?.activeLayer)) {
    return getLayerTitleFromId(map?.activeLayer);
  }
  if (map?.activeField) {
    const f = preloaded.find((o) => o.name === map.activeField);
    return f?.title || map.activeField || "";
  }
  return "";
}

function getSecondLegendTitle(map) {
  const d = typeof window !== "undefined" ? window.__dualLayer : null;
  const layerId = d?.layerId || map?.secondLayer?.layerId || "";
  const fieldName = d?.fieldName || map?.secondLayer?.field || "";

  if (isGridLayerId(layerId)) {
    return getLayerTitleFromId(layerId) || "Second layer";
  }
  if (fieldName && fieldName !== "val") {
    const f = preloaded.find((o) => o.name === fieldName);
    return f?.title || fieldName || "Second layer";
  }
  return getLayerTitleFromId(layerId) || "Second layer";
}

function getLegendItemsMain(map) {
  const items = map?.styleData?.breaks || [];
  return Array.isArray(items) ? items : [];
}

function getLegendItemsSecond(map) {
  const d = typeof window !== "undefined" ? window.__dualLayer : null;
  const items = d?.legend?.items || map?.secondLayer?.styleData?.breaks || [];
  return Array.isArray(items) ? items : [];
}

/**
 * Grid extra lines (MINDEN grid térképre)
 * - 1. sor: kisebb betűkkel
 * - 2. sor: nagyobb betűkkel
 */
function getGridExtraLinesAlwaysDeciles() {
  return [
    {
      text: "(warmer colour = more developed)",
      variant: "caption", // kisebb, mint az alap
    },
    {
      text: "Decile",
      variant: "subtitle2", // nagyobb, mint az alap
    },
  ];
}

function fixSettlements1785FirstRangeLabel(map, idx, name) {
  if (idx !== 0) return name;

  const s = String(name || "").trim();
  const layerId = map?.activeLayer || map?.wmsName || map?.id;

  if (layerId === "allum_project:settlements_1828") {
    // csak akkor írjuk át, ha az első kategória tényleg a 0 alatti / 0-ig tartó első osztály
    if (s === "< 0") return "= 0";
    return name;
  }

  if (layerId === "allum_project:settlements_1785") {
    if (s.startsWith("<=")) return s;
    if (s.startsWith("<")) return s.replace("<", "<=");
  }

  return name;
}

function fixFirstRangeLabelForLayer(layerId, idx, name) {
  if (idx !== 0) return name;

  const s = String(name || "").trim();

  // 1828: csak akkor legyen "= 0", ha az első label "< ..." formájú
  if (layerId === "allum_project:settlements_1828") {
    if (s === "< 0") return "= 0";
    return name;
  }

  // 1785: marad a korábbi "<" -> "<="
  if (layerId === "allum_project:settlements_1785") {
    if (s.startsWith("<=")) return s;
    if (s.startsWith("<")) return s.replace("<", "<=");
  }

  return name;
}

/**
 * Flicker fix:
 * Ne DOM scrollHeight-et mérjünk (az layout-váltás miatt oszcillál),
 * hanem becsüljük a várható magasságot a legend elemek számából.
 */
function estimateLegendHeightPx(itemsCount, extraLinesCount) {
  // header + divider + padding
  const base = 54 + 14 + 24; // kb.
  const perItem = 24; // egy sor kb.
  const extra = extraLinesCount > 0 ? 18 * extraLinesCount + 8 : 0;
  return base + extra + itemsCount * perItem;
}

function LegendSection({
  title,
  items,
  isGrid,
  extraLines,
  tweakFirstItemLabelFn,
}) {
  return (
    <div style={{ minWidth: 260, maxWidth: 360 }}>
      <Typography
        variant="subtitle2"
        style={{ fontWeight: 700, marginBottom: 6 }}
      >
        {title}
      </Typography>

      {isGrid && extraLines?.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          {extraLines.map((ln, i) => (
            <Typography
              key={i}
              variant={ln.variant}
              style={{ fontStyle: i === 0 ? "italic" : "normal" }}
            >
              {ln.text}
            </Typography>
          ))}
        </div>
      )}

      <Divider style={{ marginBottom: 8 }} />

      <div style={{ maxHeight: "65vh", overflowY: "auto", paddingRight: 6 }}>
        {items.map((it, idx) => {
          const color = it?.color || "#999999";
          const rawName = it?.name ?? "";
          const name = tweakFirstItemLabelFn
            ? tweakFirstItemLabelFn(idx, rawName)
            : String(rawName);

          if (!name) return null;

          return (
            <div
              key={`${name}-${idx}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 6,
              }}
            >
              <div
                style={{
                  width: 16,
                  height: 16,
                  background: color,
                  borderRadius: 3,
                  border: "1px solid rgba(0,0,0,0.15)",
                  flex: "0 0 auto",
                }}
              />
              <Typography variant="body2" style={{ lineHeight: 1.2 }}>
                {name}
              </Typography>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LegendPanel({ map }) {
  const [minimized, setMinimized] = React.useState(false);
  const [, forceTick] = React.useState(0);

  React.useEffect(() => {
    if (typeof window === "undefined") return;

    const bump = () => forceTick((t) => t + 1);

    window.addEventListener("dualLayerChanged", bump);
    window.addEventListener("secondLegendChanged", bump);

    return () => {
      window.removeEventListener("dualLayerChanged", bump);
      window.removeEventListener("secondLegendChanged", bump);
    };
  }, []);

  const mainItems = getLegendItemsMain(map);
  const secondEnabled =
    (typeof window !== "undefined" && !!window.__dualLayer?.enabled) ||
    !!map?.secondLayer?.enabled;

  const secondItems = secondEnabled ? getLegendItemsSecond(map) : [];

  const hasMain = mainItems.length > 0;
  const hasSecond = secondEnabled && secondItems.length > 0;

  const mainIsGrid = isGridLayerId(map?.activeLayer);
  const secondLayerId =
    (typeof window !== "undefined" && window.__dualLayer?.layerId) ||
    map?.secondLayer?.layerId ||
    "";
  const secondIsGrid = isGridLayerId(secondLayerId);

  const mainTitle = getMainLegendTitle(map);
  const secondTitle = getSecondLegendTitle(map);

  // ✅ ① / ② prefix csak akkor, ha a second layer enabled
  const mainTitleDecorated = secondEnabled
    ? `① ${mainTitle || "Main layer"}`
    : mainTitle || "Main layer";

  const secondTitleDecorated = secondEnabled
    ? `② ${secondTitle || "Second layer"}`
    : secondTitle || "Second layer";

  const mainExtra = mainIsGrid ? getGridExtraLinesAlwaysDeciles() : [];
  const secondExtra = secondIsGrid ? getGridExtraLinesAlwaysDeciles() : [];

  // layout decision (no flicker)
  const [sideBySide, setSideBySide] = React.useState(false);

  React.useEffect(() => {
    const recalc = () => {
      if (!(hasMain && hasSecond)) {
        setSideBySide(false);
        return;
      }

      const vh = window.innerHeight || 900;

      const h1 = estimateLegendHeightPx(mainItems.length, mainExtra.length);
      const h2 = estimateLegendHeightPx(secondItems.length, secondExtra.length);

      // Ha egymás alatt kb. túl nagy lenne, akkor inkább egymás mellé.
      // (hysteresis: ugyanaz a feltétel mindig ugyanarra vezet -> nincs oszcilláció)
      const stackedWouldOverflow = h1 + h2 > vh * 0.78;
      setSideBySide(stackedWouldOverflow);
    };

    recalc();
    window.addEventListener("resize", recalc);
    return () => window.removeEventListener("resize", recalc);
  }, [
    hasMain,
    hasSecond,
    mainItems.length,
    secondItems.length,
    mainExtra.length,
    secondExtra.length,
  ]);

  if (!hasMain && !hasSecond) return null;

  // minimized toggle button
  if (minimized) {
    return (
      <Tooltip title="Show legend">
        <IconButton
          onClick={() => setMinimized(false)}
          style={{
            position: "fixed",
            right: 16,
            bottom: 16,
            zIndex: 9999,
            color: "gray",
          }}
          size="medium"
        >
          <LegendToggleIcon />
        </IconButton>
      </Tooltip>
    );
  }

  const panelWidth = hasMain && hasSecond ? (sideBySide ? 740 : 360) : 360;

  return (
    <Paper
      className="legend-panel"
      elevation={10}
      style={{
        position: "fixed",
        right: 16,
        bottom: 16,
        zIndex: 9999,
        width: panelWidth,
        maxWidth: "calc(100vw - 32px)",
        maxHeight: "calc(100vh - 32px)",
        overflow: "hidden",
        borderRadius: 12,
      }}
    >
      {/* header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 12px",
          background: "#f7f7f7",
          borderBottom: "1px solid rgba(0,0,0,0.08)",
        }}
      >
        <Typography variant="subtitle1" style={{ fontWeight: 800 }}>
          Legend
        </Typography>
        <IconButton
          size="small"
          onClick={() => setMinimized(true)}
          title="Minimize legend"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      {/* content */}
      <div
        style={{
          padding: 12,
          display: "flex",
          flexDirection: sideBySide ? "row" : "column",
          gap: 14,
          overflow: "auto",
          maxHeight: "calc(100vh - 110px)",
        }}
      >
        {hasMain && (
          <LegendSection
            title={mainTitleDecorated}
            items={mainItems.map((it, idx) => ({
              ...it,
              name: fixSettlements1785FirstRangeLabel(map, idx, it?.name),
            }))}
            isGrid={mainIsGrid}
            extraLines={mainExtra}
            tweakFirstItemLabelFn={(idx, name) =>
              fixSettlements1785FirstRangeLabel(map, idx, name)
            }
          />
        )}

        {hasSecond && (
          <LegendSection
            title={secondTitleDecorated}
            items={secondItems.map((it, idx) => ({
              ...it,
              name: fixFirstRangeLabelForLayer(secondLayerId, idx, it?.name),
            }))}
            isGrid={secondIsGrid}
            extraLines={secondExtra}
            tweakFirstItemLabelFn={(idx, name) =>
              fixFirstRangeLabelForLayer(secondLayerId, idx, name)
            }
          />
        )}
      </div>
    </Paper>
  );
}

export default connect(mapStateToProps)(LegendPanel);
